"use client";

import translations from "@/lib/translations.json";
import { cn } from "@/lib/utils";
import {
  CalendarDays,
  CalendarX,
  ClipboardList,
  Clock,
  Moon,
  Wallet,
} from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { useWebsiteLang } from "../context/website-lang-context";
import {
  PHONE_HEIGHT,
  PHONE_WIDTH,
  PhoneFrame,
  type NavKey,
} from "./mobile-app/phone-frame";
import {
  AbsencesScreen,
  AgendaScreen,
  NightScreen,
  PaymentsScreen,
  RecordsScreen,
  TodayScreen,
} from "./mobile-app/screens";

// Employee app section. Six features around a phone that shows the real app screens.
//
// Large screens: the feature cards sit on both sides of the phone and a feature is
// selected by hovering its card (click and keyboard work too).
// Small screens: all the features are small chips that wrap above the phone, and the
// phone fills the width. The page scrolls normally past it; tap a chip to change screen.

const TAB_ICONS = [
  CalendarDays,
  Clock,
  ClipboardList,
  Wallet,
  CalendarX,
  Moon,
] as const;

// Which button of the app's bottom bar is lit for each feature. Clock-ins are made
// from the Today screen; records, payments and absences live under "More".
const TAB_NAV: NavKey[] = ["agenda", "today", "more", "more", "more", "marks"];

const TAB_COUNT = TAB_ICONS.length;
const HOVER_QUERY = "(hover: hover) and (min-width: 1024px)";
const DESKTOP_QUERY = "(min-width: 1024px)";
const MIN_SMALL_SCREEN_SCALE = 0.5;
const MAX_SMALL_SCREEN_SCALE = 1.5;
const FALLBACK_HEADER_HEIGHT = 56;
// Space kept free when the phone is sized to the screen height: the gap between the chips
// and the phone (16), a margin under the chips and one under the phone (about 16 each).
const VERTICAL_BREATHING_ROOM = 48;

/** The website scrolls inside its own container, not the window. */
function findScroller(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
}

type TabRefs = MutableRefObject<(HTMLButtonElement | null)[]>;

export default function MobileApp() {
  const { lang } = useWebsiteLang();
  const t = translations[lang].website.mobileApp;

  const [active, setActive] = useState(0);
  const [scale, setScale] = useState(1);

  const wrapRef = useRef<HTMLDivElement>(null);
  const chipsBlockRef = useRef<HTMLDivElement>(null);
  const cardRefs: TabRefs = useRef([]);
  const chipRefs: TabRefs = useRef([]);
  const hoverTimer = useRef<number | null>(null);

  // On small screens the phone is sized to fit what is left of the screen once the chips
  // are at the top: as large as the width allows, but never taller than the space below.
  useEffect(() => {
    const wrap = wrapRef.current;
    const chips = chipsBlockRef.current;
    if (!wrap || !chips) return;
    const scroller = findScroller(wrap);
    const desktop = window.matchMedia(DESKTOP_QUERY);

    const fit = () => {
      if (desktop.matches) {
        setScale(1);
        return;
      }
      const header =
        document.querySelector<HTMLElement>("nav.fixed")?.offsetHeight ??
        FALLBACK_HEADER_HEIGHT;
      const viewportHeight = scroller?.clientHeight ?? window.innerHeight;
      const byWidth = wrap.clientWidth / PHONE_WIDTH;
      const byHeight =
        (viewportHeight - header - chips.offsetHeight - VERTICAL_BREATHING_ROOM) /
        PHONE_HEIGHT;
      setScale(
        Math.max(
          MIN_SMALL_SCREEN_SCALE,
          Math.min(byWidth, byHeight, MAX_SMALL_SCREEN_SCALE),
        ),
      );
    };

    fit();
    window.addEventListener("resize", fit);
    desktop.addEventListener("change", fit);
    // The chips wrap onto more or fewer lines with the language and the width.
    const observer = new ResizeObserver(fit);
    observer.observe(chips);
    observer.observe(wrap);
    return () => {
      window.removeEventListener("resize", fit);
      desktop.removeEventListener("change", fit);
      observer.disconnect();
    };
  }, []);

  useEffect(
    () => () => {
      if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current);
    },
    [],
  );

  function clearHover() {
    if (hoverTimer.current !== null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }

  // Hovering a card selects it; the short delay avoids flicker while the cursor
  // crosses several cards on its way somewhere else.
  function onCardEnter(index: number) {
    if (!window.matchMedia(HOVER_QUERY).matches) return;
    clearHover();
    hoverTimer.current = window.setTimeout(() => setActive(index), 80);
  }

  function onTabKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
    refs: TabRefs,
  ) {
    let next: number | null = null;
    if (event.key === "ArrowDown" || event.key === "ArrowRight")
      next = (index + 1) % TAB_COUNT;
    if (event.key === "ArrowUp" || event.key === "ArrowLeft")
      next = (index - 1 + TAB_COUNT) % TAB_COUNT;
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = TAB_COUNT - 1;
    if (next === null) return;
    event.preventDefault();
    setActive(next);
    refs.current[next]?.focus();
  }

  const screens: ReactNode[] = [
    <AgendaScreen key="agenda" t={t.screens.agenda} />,
    <TodayScreen key="today" t={t.screens.today} />,
    <RecordsScreen key="records" t={t.screens.records} />,
    <PaymentsScreen key="payments" t={t.screens.payments} />,
    <AbsencesScreen key="absences" t={t.screens.absences} />,
    <NightScreen key="night" t={t.screens.night} />,
  ];

  const renderCard = (index: number) => {
    const Icon = TAB_ICONS[index];
    const tab = t.tabs[index];
    const selected = index === active;
    return (
      <button
        key={tab.title}
        ref={(el) => {
          cardRefs.current[index] = el;
        }}
        type="button"
        role="tab"
        id={`mobile-app-tab-${index}`}
        aria-selected={selected}
        aria-controls="mobile-app-panel"
        tabIndex={selected ? 0 : -1}
        onClick={() => setActive(index)}
        onMouseEnter={() => onCardEnter(index)}
        onMouseLeave={clearHover}
        onKeyDown={(e) => onTabKeyDown(e, index, cardRefs)}
        className={cn(
          "flex min-w-0 cursor-pointer items-start gap-3.5 rounded-2xl border p-4 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500",
          selected
            ? "border-amber-500/45 bg-amber-500/[0.07]"
            : "border-white/5 bg-white/[0.03] hover:border-white/15",
        )}
      >
        <span
          className={cn(
            "grid h-9 w-9 shrink-0 place-items-center rounded-[10px] transition-colors",
            selected
              ? "bg-amber-500 text-[#0b1a4a]"
              : "bg-white/[0.07] text-white/60",
          )}
        >
          <Icon size={20} aria-hidden />
        </span>
        <span className="min-w-0">
          <span className="block text-base font-semibold text-white">
            {tab.title}
          </span>
          <span className="block text-[13.5px] text-white/60">
            {tab.description}
          </span>
        </span>
      </button>
    );
  };

  return (
    <section id="mobile-app" className="relative py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden
      >
        <div className="absolute left-[8%] top-[10%] h-96 w-96 rounded-full bg-amber-500/10 blur-[120px]" />
        <div className="absolute bottom-[5%] right-[8%] h-96 w-96 rounded-full bg-teal-500/10 blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-amber-500/20 px-4 py-1.5 text-xs font-medium text-amber-400 glass">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse-slow" />
            {t.badge}
          </div>
          <h2 className="mb-4 text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            {t.titlePrefix}{" "}
            <span className="text-gradient-amber">{t.titleHighlight}</span>
          </h2>
          <p className="mx-auto text-balance text-base text-white/50 md:text-lg">
            {t.subtitle}
          </p>
        </div>

        <div
          ref={wrapRef}
          className="mt-8 flex flex-col gap-5 lg:mt-14 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:gap-11"
        >
          <div
            role="tablist"
            aria-orientation="vertical"
            aria-label={t.tabListLabel}
            className="hidden gap-2.5 lg:grid"
          >
            {[0, 1, 2].map(renderCard)}
          </div>

          <div className="flex flex-col items-center gap-4">
            <div ref={chipsBlockRef} className="w-full lg:hidden">
              <div
                role="tablist"
                aria-label={t.tabListLabel}
                className="flex flex-wrap justify-center gap-1.5"
              >
                {t.tabs.map((tab, index) => {
                  const Icon = TAB_ICONS[index];
                  const selected = index === active;
                  return (
                    <button
                      key={tab.title}
                      ref={(el) => {
                        chipRefs.current[index] = el;
                      }}
                      type="button"
                      role="tab"
                      id={`mobile-app-chip-${index}`}
                      aria-selected={selected}
                      aria-controls="mobile-app-panel"
                      tabIndex={selected ? 0 : -1}
                      onClick={() => setActive(index)}
                      onKeyDown={(e) => onTabKeyDown(e, index, chipRefs)}
                      className={cn(
                        "flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500",
                        selected
                          ? "border-amber-500 bg-amber-500 text-[#0b1a4a]"
                          : "border-white/10 bg-white/5 text-white/60",
                      )}
                    >
                      <Icon size={13} aria-hidden />
                      {tab.title}
                    </button>
                  );
                })}
              </div>
              <div
                aria-live="polite"
                className="mt-3 min-h-5 text-balance text-center text-[13px] leading-5 text-white/60"
              >
                {t.tabs[active].description}
              </div>
            </div>

            <div
              id="mobile-app-panel"
              role="tabpanel"
              aria-labelledby={`mobile-app-tab-${active}`}
            >
              <PhoneFrame
                nav={TAB_NAV[active]}
                labels={t.nav}
                scale={scale}
                screenKey={active}
              >
                {screens[active]}
              </PhoneFrame>
            </div>
          </div>

          <div
            role="tablist"
            aria-orientation="vertical"
            aria-label={t.tabListLabel}
            className="hidden gap-2.5 lg:grid"
          >
            {[3, 4, 5].map(renderCard)}
          </div>
        </div>
      </div>
    </section>
  );
}
